import {
  DEFAULT_CASHBOOK_CATEGORIES,
  listSelectableCashbookCategories,
  type CashbookCategory,
  type CashbookFundingSourcePostable
} from '@finvox/shared/cashbook';
import { parseMoneyInput, sumMoney } from '@finvox/shared/money';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookCategorySelect } from '@/components/cashbook/CashbookCategorySelect';
import { CashbookFundingSourcePicker } from '@/components/cashbook/CashbookFundingSourcePicker';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { displayCashbookEntryLabel } from '@/lib/cashbookEntryLabel';
import { cn } from '@/lib/cn';

export type CashbookIncomeMode = 'drawer' | 'custom';

export type CashbookDrawerExpenseLine = {
  id: string;
  categoryKey: string;
  amount: string;
};

export type CashbookDrawerPriorLine = {
  id: string;
  drawerCount: string;
};

type Props = {
  type: 'in' | 'out';
  amount: string;
  categoryKey: string;
  date: string;
  memo: string;
  fundingSource: CashbookFundingSourcePostable | '';
  /** Income only: left in drawer vs custom income */
  incomeMode: CashbookIncomeMode | '';
  /** Calendar today (YYYY-MM-DD) — max selectable date */
  todayDate: string;
  /** Earliest selectable date (opening / period start) */
  minDate?: string;
  /** Org custom categories from settings (merged into expense picker) */
  customCategories?: CashbookCategory[];
  /** Prior left-in-drawer lines already saved for the selected date */
  drawerPriorLines?: CashbookDrawerPriorLine[];
  /** Sum of prior drawerCounts (or 0.00 if none) */
  drawerPriorSum?: string;
  drawerExpenseSum?: string;
  /** From-drawer expense lines for the selected date */
  drawerExpenseLines?: CashbookDrawerExpenseLine[];
  /** Block left-in-drawer when PayVox/manual sales already exist for that date */
  drawerBlockedReason?: 'payvox' | 'manual_sales' | null;
  busy: boolean;
  disabled: boolean;
  embedded?: boolean;
  onCancel?: () => void;
  onTypeChange: (t: 'in' | 'out') => void;
  onAmountChange: (v: string) => void;
  onCategoryChange: (v: string) => void;
  onDateChange: (v: string) => void;
  onMemoChange: (v: string) => void;
  onFundingSourceChange: (v: CashbookFundingSourcePostable) => void;
  onIncomeModeChange: (v: CashbookIncomeMode) => void;
  onAdd: () => void;
};

export function CashbookQuickAdd({
  type,
  amount,
  categoryKey,
  date,
  memo,
  fundingSource,
  incomeMode,
  todayDate,
  minDate,
  customCategories = [],
  drawerPriorLines = [],
  drawerPriorSum = '0.00',
  drawerExpenseSum,
  drawerExpenseLines = [],
  drawerBlockedReason,
  busy,
  disabled,
  embedded = false,
  onCancel,
  onTypeChange,
  onAmountChange,
  onCategoryChange,
  onDateChange,
  onMemoChange,
  onFundingSourceChange,
  onIncomeModeChange,
  onAdd
}: Props) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language.startsWith('ar');
  const isDrawerIncome = type === 'in' && incomeMode === 'drawer';
  const isCustomIncome = type === 'in' && incomeMode === 'custom';

  const expenseCategoryOptions = useMemo(() => {
    return listSelectableCashbookCategories('out', customCategories).map((c) => ({
      value: c.key,
      label: DEFAULT_CASHBOOK_CATEGORIES.some((d) => d.key === c.key)
        ? t(`cashbook.categories.${c.key}`)
        : c.key
    }));
  }, [customCategories, t]);

  const incomeCategoryOptions = useMemo(() => {
    return listSelectableCashbookCategories('in', customCategories).map((c) => ({
      value: c.key,
      label: DEFAULT_CASHBOOK_CATEGORIES.some((d) => d.key === c.key)
        ? t(`cashbook.categories.${c.key}`)
        : c.key
    }));
  }, [customCategories, t]);

  const expenseSum = drawerExpenseSum ?? '0.00';
  const soFar = useMemo(
    () => sumMoney([drawerPriorSum, expenseSum]),
    [drawerPriorSum, expenseSum]
  );
  const hasNewAmount = amount.trim() !== '';
  const newEntryAmount = hasNewAmount ? parseMoneyInput(amount) : null;
  const dayTotal =
    newEntryAmount != null ? sumMoney([soFar, newEntryAmount]) : soFar;

  const incomeOptions: { id: CashbookIncomeMode; label: string }[] = [
    { id: 'drawer', label: t('cashbook.incomeMode.leftInDrawer') },
    { id: 'custom', label: t('cashbook.incomeMode.customIncome') }
  ];

  const canSubmit =
    !disabled &&
    !busy &&
    (type === 'out'
      ? (fundingSource === 'drawer' || fundingSource === 'treasury') &&
        categoryKey.trim() !== ''
      : incomeMode === 'drawer'
        ? !drawerBlockedReason && amount.trim() !== ''
        : incomeMode === 'custom' && categoryKey.trim() !== '');

  return (
    <div
      className={cn(
        embedded
          ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
          : 'flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end',
        !embedded && 'mt-6 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4'
      )}
    >
      <div className={cn('w-full', embedded && 'md:col-span-2')}>
        <SegmentedControl
          className="w-full"
          items={[
            { id: 'in', label: t('cashbook.typeIn') },
            { id: 'out', label: t('cashbook.typeOut') }
          ]}
          value={type}
          onChange={(id) => onTypeChange(id as 'in' | 'out')}
          aria-label={t('cashbook.category')}
        />
      </div>

      {type === 'in' ? (
        <fieldset className={cn('w-full', embedded && 'md:col-span-2')} disabled={disabled}>
          <legend className="text-xs text-[var(--fv-text-muted)]">
            {t('cashbook.incomeMode.label')} *
          </legend>
          <div className="mt-2 flex flex-col gap-2 sm:flex-row" role="radiogroup">
            {incomeOptions.map((opt) => {
              const selected = incomeMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => onIncomeModeChange(opt.id)}
                  className={cn(
                    'min-h-11 w-full rounded-lg border px-4 py-3 text-sm font-medium transition-colors',
                    selected
                      ? 'border-[var(--fv-accent)] bg-[var(--fv-accent-muted,var(--fv-surface-2))] text-[var(--fv-text)]'
                      : 'border-[var(--fv-border)] bg-[var(--fv-surface)] text-[var(--fv-text)] hover:border-[var(--fv-text-muted)]'
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      ) : null}

      {type === 'out' || incomeMode ? (
        <>
          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">
              {isDrawerIncome ? t('cashbook.drawerCount.label') : t('cashbook.amount')}
            </label>
            <MoneyInput
              className={cn('mt-1 w-full', !embedded && 'sm:w-32')}
              allowEmpty
              value={amount}
              onValueChange={onAmountChange}
              disabled={disabled || (isDrawerIncome && Boolean(drawerBlockedReason))}
              aria-label={isDrawerIncome ? t('cashbook.drawerCount.label') : t('cashbook.amount')}
            />
          </div>

          {type === 'out' ? (
            <div className="w-full">
              <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.category')}</label>
              <CashbookCategorySelect
                className={cn('mt-1 w-full', !embedded && 'sm:w-40')}
                value={categoryKey}
                onValueChange={onCategoryChange}
                categories={expenseCategoryOptions}
                allowCreate
                disabled={disabled}
              />
            </div>
          ) : null}

          {isCustomIncome ? (
            <div className="w-full">
              <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.category')}</label>
              <CashbookCategorySelect
                className={cn('mt-1 w-full', !embedded && 'sm:w-40')}
                value={categoryKey}
                onValueChange={onCategoryChange}
                categories={incomeCategoryOptions}
                allowCreate
                disabled={disabled}
              />
            </div>
          ) : null}

          <div className="w-full">
            <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colDate')}</label>
            <DatePicker
              className={cn('mt-1 w-full', !embedded && 'sm:w-44')}
              value={date}
              onChange={onDateChange}
              disabled={disabled}
              min={minDate}
              max={todayDate}
            />
            <p className="mt-1 text-xs text-[var(--fv-text-muted)]">
              {t('cashbook.incomeMode.noFutureDates')}
            </p>
          </div>

          <div
            className={cn(
              'w-full min-w-0',
              embedded && 'md:col-span-2',
              !embedded && 'sm:min-w-[10rem] sm:flex-1'
            )}
          >
            <label className="text-xs text-[var(--fv-text-muted)]">
              {isDrawerIncome ? t('cashbook.drawerCount.notes') : t('cashbook.memo')}
            </label>
            <Input
              className="mt-1 w-full"
              value={memo}
              onChange={(e) => onMemoChange(e.target.value)}
              disabled={disabled || (isDrawerIncome && Boolean(drawerBlockedReason))}
              maxLength={500}
            />
          </div>

          {type === 'out' ? (
            <div className={cn('w-full', embedded && 'md:col-span-2')}>
              <CashbookFundingSourcePicker
                value={fundingSource}
                onChange={onFundingSourceChange}
                disabled={disabled}
                required
              />
            </div>
          ) : null}

          {isDrawerIncome && drawerBlockedReason ? (
            <p
              className={cn(
                'rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950',
                embedded && 'md:col-span-2'
              )}
            >
              {drawerBlockedReason === 'payvox'
                ? t('cashbook.drawerCount.blockedPayvox')
                : t('cashbook.drawerCount.blockedManualSales')}
            </p>
          ) : null}

          {isDrawerIncome && !drawerBlockedReason ? (
            <div
              className={cn(
                'space-y-4 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-bg)]/40 p-3',
                embedded && 'md:col-span-2'
              )}
            >
              <div>
                <p className="text-xs font-medium text-[var(--fv-text-muted)]">
                  {t('cashbook.drawerCount.priorHeading')}
                </p>
                {drawerPriorLines.length === 0 ? (
                  <p className="mt-2 text-sm text-[var(--fv-text-muted)]">
                    {t('cashbook.drawerCount.priorEmpty')}
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {drawerPriorLines.map((line) => (
                      <li key={line.id} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 text-[var(--fv-text-muted)]">•</span>
                        <MoneyDisplay
                          value={line.drawerCount}
                          className="ms-auto shrink-0 tabular-nums text-[var(--fv-text)]"
                        />
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 flex items-center justify-between gap-3 border-t border-[var(--fv-border)] pt-2 text-sm">
                  <span className="text-[var(--fv-text-muted)]">
                    {t('cashbook.drawerCount.priorSubtotal')}
                  </span>
                  <MoneyDisplay
                    value={drawerPriorSum}
                    className="font-medium tabular-nums text-[var(--fv-text)]"
                  />
                </p>
              </div>

              <div>
                <p className="text-xs font-medium text-[var(--fv-text-muted)]">
                  {t('cashbook.drawerCount.expensesHeading')}
                </p>
                {drawerExpenseLines.length === 0 ? (
                  <p className="mt-2 text-sm text-[var(--fv-text-muted)]">
                    {t('cashbook.drawerCount.expensesEmpty')}
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {drawerExpenseLines.map((line) => (
                      <li key={line.id} className="flex items-start justify-between gap-3">
                        <span className="min-w-0 break-words text-[var(--fv-text)]">
                          {displayCashbookEntryLabel(line.categoryKey, t, isArabic)}
                        </span>
                        <MoneyDisplay
                          value={line.amount}
                          className="shrink-0 tabular-nums text-[var(--fv-text)]"
                        />
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 flex items-center justify-between gap-3 border-t border-[var(--fv-border)] pt-2 text-sm">
                  <span className="text-[var(--fv-text-muted)]">
                    {t('cashbook.drawerCount.expensesTotal')}
                  </span>
                  <MoneyDisplay
                    value={expenseSum}
                    className="font-medium tabular-nums text-[var(--fv-text)]"
                  />
                </p>
              </div>

              <div className="space-y-1.5 border-t border-[var(--fv-border)] pt-3 text-sm">
                <p className="flex items-center justify-between gap-3">
                  <span className="text-[var(--fv-text-muted)]">
                    {t('cashbook.drawerCount.soFar')}
                  </span>
                  <MoneyDisplay
                    value={soFar}
                    className="font-medium tabular-nums text-[var(--fv-text)]"
                  />
                </p>
                {hasNewAmount && newEntryAmount != null ? (
                  <p className="flex items-center justify-between gap-3">
                    <span className="text-[var(--fv-text-muted)]">
                      + {t('cashbook.drawerCount.newEntry')}
                    </span>
                    <MoneyDisplay
                      value={newEntryAmount}
                      className="font-medium tabular-nums text-[var(--fv-text)]"
                    />
                  </p>
                ) : null}
                <p className="flex items-center justify-between gap-3 border-t border-[var(--fv-border)] pt-2">
                  <span className="font-medium text-[var(--fv-text)]">
                    = {t('cashbook.drawerCount.dayTotal')}
                  </span>
                  <MoneyDisplay
                    value={dayTotal}
                    className="font-semibold tabular-nums text-[var(--fv-text)]"
                  />
                </p>
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      <div
        className={cn(
          'flex w-full justify-end gap-2',
          embedded ? 'md:col-span-2' : 'sm:w-auto'
        )}
      >
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button type="button" loading={busy} disabled={!canSubmit} onClick={onAdd} className="min-h-11">
          {isDrawerIncome ? t('cashbook.drawerCount.save') : t('cashbook.add')}
        </Button>
      </div>
    </div>
  );
}
